import React from 'react';
import FocusTrap from 'focus-trap-react';
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import './NotificationModal.css';

interface NotificationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  buttonText?: string;
  onClose: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  title,
  message,
  type = 'info',
  buttonText = 'OK',
  onClose,
  autoClose = false,
  autoCloseDelay = 3000
}) => {
  React.useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <SuccessIcon className={`notification-icon ${type}`} />;
      case 'error':
        return <ErrorIcon className={`notification-icon ${type}`} />;
      case 'warning':
        return <WarningIcon className={`notification-icon ${type}`} />;
      case 'info':
        return <InfoIcon className={`notification-icon ${type}`} />;
      default:
        return <InfoIcon className={`notification-icon ${type}`} />;
    }
  };

  const getButtonClass = () => {
    switch (type) {
      case 'success':
        return 'btn-success';
      case 'error':
        return 'btn-danger';
      case 'warning':
        return 'btn-warning';
      case 'info':
        return 'btn-primary';
      default:
        return 'btn-primary';
    }
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: true
      }}
    >
      <div className="modal-overlay" onClick={handleOverlayClick} role="presentation">
        <div 
          className={`notification-modal-container ${type}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-modal-title"
          aria-describedby="notification-modal-message"
        >
          <div className="notification-modal-header">
            <div className="notification-title-section">
              {getIcon()}
              <h2 id="notification-modal-title" className="notification-title">{title}</h2>
            </div>
            <button 
              className="modal-close-btn" 
              onClick={onClose}
              aria-label="Close notification"
            >
              <CloseIcon aria-hidden="true" />
            </button>
          </div>

          <div className="notification-modal-body">
            <p id="notification-modal-message" className="notification-message">{message}</p>
          </div>

          <div className="notification-modal-actions">
            <button
              type="button"
              onClick={onClose}
              className={`btn ${getButtonClass()}`}
              aria-label={buttonText}
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
};

export default NotificationModal;
